/**
 * Pure helpers for the optional second salary (docs/prd/probation-salary-review.md
 * §4, FE scope §7.1–§7.3). Dates here are UTC-midnight epoch ms, same
 * convention as the rest of onboarding (features/datepicker/date-picker.jsx
 * builds `Date.UTC(y, m-1, d)`).
 */

/**
 * Adds `months` to a UTC-midnight date, keeping the day of month and
 * clamping to the target month's last day when it doesn't have that many
 * days (2026-01-31 + 1 month -> 2026-02-28). Mirrors the backend's
 * `addMonthsClamped` (docs/prd/probation-salary-review.md §3) — the FE only
 * needs its own copy to prefill the date picker; the backend stays the
 * source of truth for anything sent to the server.
 */
export function addMonthsClamped(dateMs: number, months: number): number {
  const d = new Date(dateMs);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const day = d.getUTCDate();
  const targetMonthIndex = month + months;
  // Day 0 of the month AFTER the target = the target month's last day.
  const lastDayOfTargetMonth = new Date(Date.UTC(year, targetMonthIndex + 1, 0)).getUTCDate();
  const clampedDay = Math.min(day, lastDayOfTargetMonth);
  return Date.UTC(year, targetMonthIndex, clampedDay);
}

/**
 * The default second-salary date offered to the editor: the day after
 * probation ends. Per §3, `defaultProbationEndDate = addMonthsClamped(hiringDate,
 * probationMonths) − 1 day`, so "the day after" is `addMonthsClamped` itself.
 */
export function defaultSalaryChangeDate(hiringDate: number, probationMonths: number): number {
  return addMonthsClamped(hiringDate, probationMonths);
}

/**
 * `onboarding-form.tsx` `buildPayload` (§7.1): the checkbox is UI-only —
 * unchecked always sends both fields as null, regardless of what is left in
 * the (now hidden) date/amount inputs.
 */
export function buildSalaryChangePayload(
  data: Record<string, any>
): { salaryChangeDate: number | null; salaryAfterChange: number | null } {
  if (!data.hasSalaryChange) {
    return { salaryChangeDate: null, salaryAfterChange: null };
  }
  const salaryChangeDate = typeof data.salaryChangeDate === "number" ? data.salaryChangeDate : null;
  const rawAmount = data.salaryAfterChange;
  const salaryAfterChange =
    typeof rawAmount === "number"
      ? rawAmount
      : rawAmount != null && rawAmount !== ""
        ? parseFloat(rawAmount) || null
        : null;
  return { salaryChangeDate, salaryAfterChange };
}

/**
 * FE mirror of the backend's validation (§4): both null or both set (already
 * guaranteed by `buildSalaryChangePayload`), amount > 0, date after hiring
 * and, when the contract has an end date, not after it. Returns the first
 * problem found, or null. Checked at submit time so the user gets an
 * immediate message instead of a round trip for a mistake the FE can already
 * see.
 */
export function validateSalaryChangePayload(
  payload: { salaryChangeDate: number | null; salaryAfterChange: number | null },
  context: { hiringDate: number | null; contractEndDate: number | null }
): string | null {
  const { salaryChangeDate, salaryAfterChange } = payload;
  if (salaryChangeDate == null && salaryAfterChange == null) return null;
  if (salaryChangeDate == null || salaryAfterChange == null) return "salary_change_invalid";
  if (!(salaryAfterChange > 0)) return "salary_change_invalid";
  if (context.hiringDate != null && salaryChangeDate <= context.hiringDate) return "salary_change_invalid";
  if (context.contractEndDate != null && salaryChangeDate > context.contractEndDate) return "salary_change_invalid";
  return null;
}

/**
 * Maps a `400 SALARY_CHANGE_INVALID` response to a translation key: the
 * generic date/amount message, or a specific one when the backend names why
 * (`reason: 'custom_contract_template'` — the company's contract template
 * cannot print the two-period clause at all, no date/amount fix will help).
 */
export function salaryChangeErrorMessageKey(e: any): string {
  const data = e?.response?.data;
  if (data?.reason === "custom_contract_template") {
    return "salary_change_invalid_custom_template";
  }
  return "salary_change_invalid";
}

/**
 * The `{date, amount}` shown next to the current salary (onboarding record
 * view §7.2, ContractAmendmentsPanel's `currentTerms.plannedSalaryChange`) —
 * only while the change is still ahead of `now`. A change date in the past
 * means the second salary already applies (the current-salary helper takes
 * over); showing it as "planned" again would be confusing.
 */
export function computePlannedSalaryChange(
  salaryChangeDate: number | null | undefined,
  salaryAfterChange: number | null | undefined,
  now: number = Date.now()
): { date: number; amount: number } | null {
  if (salaryChangeDate == null || salaryAfterChange == null) return null;
  if (salaryChangeDate <= now) return null;
  return { date: salaryChangeDate, amount: salaryAfterChange };
}
